class ThemeDialog extends HTMLElement {
  static instanceCount = 0;
  static closeDurationMs = 300;

  constructor() {
    super();
    this.dialog = null;
    this.triggers = [];
    this.lastTrigger = null;
    this.openedByKeyboard = false;
    this.listenerController = null;
    this.isInitialized = false;
    this.isClosing = false;
    this.closeTimer = null;

    this.onDialogClose = this.onDialogClose.bind(this);
    this.onDialogCancel = this.onDialogCancel.bind(this);
    this.onDialogClick = this.onDialogClick.bind(this);
    this.onInternalClick = this.onInternalClick.bind(this);
  }

  connectedCallback() {
    if (this.isInitialized) return;

    this.dialog = this.querySelector("dialog");
    if (!this.dialog) return;

    if (!this.dialog.id) {
      ThemeDialog.instanceCount += 1;
      this.dialog.id = `theme-dialog-${ThemeDialog.instanceCount}`;
    }

    this.listenerController = new AbortController();
    this.triggers = Array.from(document.querySelectorAll(`[data-toggle="${this.dialog.id}"]`));

    this.triggers.forEach((trigger) => {
      trigger.setAttribute("aria-controls", this.dialog.id);
      trigger.setAttribute("aria-expanded", "false");
      trigger.setAttribute("aria-haspopup", "dialog");
      trigger.addEventListener("click", this.onTriggerClick.bind(this, trigger), {
        signal: this.listenerController.signal,
      });
    });

    this.dialog.addEventListener("close", this.onDialogClose, { signal: this.listenerController.signal });
    this.dialog.addEventListener("cancel", this.onDialogCancel, { signal: this.listenerController.signal });
    this.dialog.addEventListener("click", this.onDialogClick, { signal: this.listenerController.signal });
    this.addEventListener("click", this.onInternalClick, { signal: this.listenerController.signal });
    this.openFromUrlIfNeeded();
    this.isInitialized = true;
  }

  disconnectedCallback() {
    this.unlockPageScroll();

    if (this.listenerController) {
      this.listenerController.abort();
      this.listenerController = null;
    }

    this.dialog = null;
    this.triggers = [];
    this.lastTrigger = null;
    this.openedByKeyboard = false;
    this.isInitialized = false;
    this.isClosing = false;

    if (this.closeTimer) {
      window.clearTimeout(this.closeTimer);
      this.closeTimer = null;
    }
  }

  setExpandedState(isExpanded) {
    this.triggers.forEach((trigger) => {
      trigger.setAttribute("aria-expanded", String(isExpanded));
    });
  }

  onTriggerClick(trigger, event) {
    event.preventDefault();
    this.lastTrigger = trigger;
    this.openedByKeyboard = event.detail === 0;
    this.openDialog();
  }

  openDialog() {
    if (this.dialog.open) {
      this.requestClose();
      return;
    }

    this.closeOtherOpenDialogs();
    this.dialog.classList.remove("is-closing");
    this.isClosing = false;

    try {
      this.dialog.showModal();
    } catch (error) {
      console.error("Failed to open dialog:", error);
      if (!this.dialog.open && this.dialog.isConnected) {
        this.dialog.setAttribute("open", "");
      }
    }

    this.dialog.dataset.openedBy = this.openedByKeyboard ? "keyboard" : "pointer";
    this.setExpandedState(this.dialog.open);

    if (this.dialog.open) {
      this.lockPageScroll();
    }
  }

  openFromUrlIfNeeded() {
    if (!this.dialog.id) return;

    const dialogName = this.dialog.id.endsWith("-dialog")
      ? this.dialog.id.slice(0, -"-dialog".length)
      : this.dialog.id;
    const params = new URLSearchParams(window.location.search);
    const hash = window.location.hash;
    const shouldOpenFromQuery = params.get(dialogName) === "open";
    const shouldOpenFromHash = hash === `#${this.dialog.id}` || hash === `#${dialogName}`;

    if (!shouldOpenFromQuery && !shouldOpenFromHash) return;

    window.setTimeout(() => {
      this.openDialog();
    }, 250);
  }

  onDialogClose() {
    this.dialog.classList.remove("is-closing");
    delete this.dialog.dataset.openedBy;
    this.setExpandedState(false);
    this.isClosing = false;
    this.unlockPageScroll();

    if (this.closeTimer) {
      window.clearTimeout(this.closeTimer);
      this.closeTimer = null;
    }

    if (this.lastTrigger) {
      this.lastTrigger.focus();
    }
  }

  onDialogCancel(event) {
    event.preventDefault();
    this.requestClose();
  }

  onDialogClick(event) {
    if (event.target !== this.dialog) return;

    const rect = this.dialog.getBoundingClientRect();
    const clickedOutside = event.clientX < rect.left ||
      event.clientX > rect.right ||
      event.clientY < rect.top ||
      event.clientY > rect.bottom;

    if (clickedOutside) {
      this.requestClose();
    }
  }

  onInternalClick(event) {
    const closeButton = event.target.closest("[data-dialog-close]");

    if (!closeButton || !this.contains(closeButton) || !this.dialog.open) return;

    this.requestClose();
  }

  requestClose() {
    if (!this.dialog || !this.dialog.open || this.isClosing) return;

    const closeDurationMs = this.getCloseDurationMs();

    if (closeDurationMs === 0) {
      this.dialog.classList.remove("is-closing");
      this.dialog.close();
      return;
    }

    this.isClosing = true;
    this.dialog.classList.add("is-closing");

    if (this.closeTimer) {
      window.clearTimeout(this.closeTimer);
    }

    this.closeTimer = window.setTimeout(() => {
      this.closeTimer = null;

      if (this.dialog.open) {
        this.dialog.close();
      }
    }, closeDurationMs);
  }

  getCloseDurationMs() {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      return 0;
    }

    const computedStyle = window.getComputedStyle(this.dialog);
    const cssDuration = computedStyle.getPropertyValue("--dialog-transition-duration").trim();
    const parsedDuration = Number.parseFloat(cssDuration);

    if (!Number.isFinite(parsedDuration) || parsedDuration < 0) {
      return ThemeDialog.closeDurationMs;
    }

    if (cssDuration.endsWith("ms")) {
      return parsedDuration;
    }

    return parsedDuration * 1000;
  }

  closeOtherOpenDialogs() {
    document.querySelectorAll("dialog[open]").forEach((openDialog) => {
      let dialogHost = null;

      if (openDialog === this.dialog) return;

      let node = openDialog.parentElement;
      while (node) {
        if (typeof node.requestClose === "function") {
          dialogHost = node;
          break;
        }

        node = node.parentElement;
      }

      if (dialogHost && typeof dialogHost.requestClose === "function") {
        dialogHost.requestClose();
        return;
      }

      openDialog.close();
    });
  }

  lockPageScroll() {
    if (document.body.dataset.scrollLocked === "true") return;

    const scrollY = window.scrollY;

    document.body.dataset.scrollLocked = "true";
    document.body.dataset.scrollLockTop = String(scrollY);
    document.body.style.position = "fixed";
    document.body.style.top = `-${scrollY}px`;
    document.body.style.left = "0";
    document.body.style.right = "0";
    document.body.style.width = "100%";
  }

  unlockPageScroll() {
    if (document.body.dataset.scrollLocked !== "true") return;

    const scrollY = Number(document.body.dataset.scrollLockTop || 0);
    const documentElement = document.documentElement;
    const scrollBehavior = documentElement.style.scrollBehavior;

    delete document.body.dataset.scrollLocked;
    delete document.body.dataset.scrollLockTop;
    document.body.style.position = "";
    document.body.style.top = "";
    document.body.style.left = "";
    document.body.style.right = "";
    document.body.style.width = "";
    documentElement.style.scrollBehavior = "auto";
    window.scrollTo(0, scrollY);
    documentElement.style.scrollBehavior = scrollBehavior;
  }
}

if (!customElements.get("theme-dialog")) {
  customElements.define("theme-dialog", ThemeDialog);
}

class ThemeCollapse extends HTMLElement {
  constructor() {
    super();
    this.handleClick = this.handleClick.bind(this);
    this.reduceMotionQuery = window.matchMedia("(prefers-reduced-motion: reduce)");
  }

  connectedCallback() {
    this.details = this.querySelector(".theme-collapse-details");
    this.summary = this.querySelector(".theme-collapse-summary");

    if (!this.details || !this.summary) return;

    this.summary.addEventListener("click", this.handleClick);
  }

  disconnectedCallback() {
    if (this.summary) {
      this.summary.removeEventListener("click", this.handleClick);
    }
  }

  handleClick(event) {
    event.preventDefault();

    if (!this.details || this.details.dataset.animating === "true") return;

    this.animate();
  }

  cleanup() {
    this.details.style.height = "";
    this.details.style.overflow = "";
    this.details.dataset.animating = "false";
  }

  animate() {
    const isOpen = this.details.hasAttribute("open");
    const startHeight = this.details.offsetHeight;
    let endHeight = this.summary.offsetHeight;

    if (this.reduceMotionQuery.matches) {
      this.details.toggleAttribute("open", !isOpen);
      return;
    }

    if (!isOpen) {
      this.details.style.height = `${startHeight}px`;
      this.details.setAttribute("open", "");
      endHeight = this.details.scrollHeight;
    }

    this.details.dataset.animating = "true";
    this.details.style.overflow = "hidden";
    this.details.style.height = `${startHeight}px`;

    window.requestAnimationFrame(() => {
      this.details.style.height = `${endHeight}px`;
    });

    const fallbackTimer = window.setTimeout(() => {
      if (isOpen) {
        this.details.removeAttribute("open");
      }
      this.cleanup();
    }, 300);

    const handleTransitionEnd = (event) => {
      if (event.target !== this.details || event.propertyName !== "height") return;

      window.clearTimeout(fallbackTimer);
      this.details.removeEventListener("transitionend", handleTransitionEnd);

      if (isOpen) {
        this.details.removeAttribute("open");
      }
      this.cleanup();
    };

    this.details.addEventListener("transitionend", handleTransitionEnd);
  }
}

if (!customElements.get("theme-collapse")) {
  customElements.define("theme-collapse", ThemeCollapse);
}
